import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, Navigate } from 'react-router-dom'
import { Camera, FileText, X, Plus, File, Film, Video, Check, AlertCircle, Trash2, Stethoscope, RefreshCw, Image as ImageIcon, Activity, ChevronDown, ChevronRight, Upload } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import DeleteCaseModal from '../../components/DeleteCaseModal'
import { isHeldByDoctor, reportDeleteFailure } from '../../utils/caseDelete'
import MediaLightbox from '../../components/MediaLightbox'
import StoredDocViewer, { storedDocIsPdf } from '../../components/StoredDocViewer' // s101 TF66: in-app PDF view
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import SubmitOverlay from '../../components/SubmitOverlay'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import orellaOWhite from '../../assets/logos/orella_o_white.png'
import { formatSpecialtyName, formatCaseSpecialty } from '../../utils/specialtyResolver'
import { useQuestionnaireTranslation } from '../../i18n/questionnaires/useQuestionnaireTranslation'
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import { priorRecordRows, addedRecordKeys } from '../../utils/priorTests'
import { aiAPI } from '../../services/api'
import { expectsDetectPrep, detectPrepPhase, hasDetectPrepContent } from './detectPrepState'
import { captureCardKey, recordCardKey, pinnedCardKey, splitUploads, showNothingToAdd, detectSaveData } from './detectUploads' // PROD-1198/1199
import { budgetSuggestions } from './detectSuggestions' // s126 A2: at most 3 suggestion cards before "N more"
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import { caseIsShared } from '../../utils/summaryNext' // s98: Detect before share
import { mergeFreshUrls, detectListsOf } from '../../utils/freshFileUrls'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import ScoredScale, { parseScale, scaleScore, scaleTotalText } from '../../components/describe/ScoredScale' // s126 A1
import scalesLib from '../../data/scales.json' // s126 A1: the free Detect scales (NLHQ, TNSS) + the diagnosis map
import { pickScaleOffer, savedPatientScales, scaleText } from '../../utils/scaleOffer' // s126 A1

// Color constants — Detect phase
const NAVY = '#15354F'
const BLUE = '#46A8F9'
const LIGHT_BLUE = '#A7D9FC'
const ICE_BLUE = '#E9F8FF'

// v206.1: SPECIALTY_KEY_MAP + formatSpecialtyName moved to shared utils/specialtyResolver.js

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// ===== Section Header — unified pattern =====
const SectionHeader = ({ icon, title, count, color = BLUE, solidIconBg = false }) => (
  <div className="flex items-center gap-2 mb-2">
    <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: solidIconBg ? color : (color + '1A') }}>
      {icon}
    </div>
    <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color: NAVY }}>{title}</span>
    {count > 0 && (
      <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md ml-auto" style={{ backgroundColor: color }}>{count}</span>
    )}
  </div>
)

// v218.0.3 Stage 2: HelpfulUploadRow removed — replaced by native three-zone
// rendering (Zone 1 capture cards / Zone 2 prior-records rows) driven directly
// by patient_summary.detectPrep instead of frontend keyword-regex heuristics.

// v228.15.6: Browsers block navigating the top frame to a data: URL
// ("Not allowed to navigate top frame to data URL"). Small docs are stored as
// base64 data URLs, so convert those to a Blob object URL (which IS allowed)
// before opening. Real storage (http) URLs open directly. Falls back to a
// download if the popup is blocked.
const dataUrlToBlob = (dataUrl) => {
  const comma = dataUrl.indexOf(',')
  const header = dataUrl.slice(0, comma)
  const isBase64 = /;base64/i.test(header)
  const mime = (header.match(/data:([^;]+)/) || [])[1] || 'application/octet-stream'
  const body = dataUrl.slice(comma + 1)
  let bytes
  if (isBase64) {
    const binary = atob(body)
    bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  } else {
    bytes = new TextEncoder().encode(decodeURIComponent(body))
  }
  return new Blob([bytes], { type: mime })
}

const openDocument = (url, fileName) => {
  if (!url) return
  try {
    if (url.startsWith('data:')) {
      const blobUrl = URL.createObjectURL(dataUrlToBlob(url))
      const win = window.open(blobUrl, '_blank')
      if (!win) {
        // Popup blocked — fall back to a download so the patient still gets the file.
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = fileName || 'document'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      // Revoke after the new tab has had time to load.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } else {
      window.open(url, '_blank')
    }
  } catch (e) {
    // Last-resort: attempt a plain open so behavior is never worse than before.
    try { window.open(url, '_blank') } catch (_) {}
  }
}

// ===== File Row — 112px thumbnails, playable video, viewable docs, MD names =====
// v143.4: Fixed video playback (was no-op on tap) and PDF blank tab issue
const FileRow = ({ item, onRemove, mdName, onView, onBroken }) => {
  const { t } = useI18n()
  const [showVideoModal, setShowVideoModal] = useState(false)
  const [showImageModal, setShowImageModal] = useState(false)
  // ★ s101 (TestFlight b66, Dr. Rami: "Does not open when clicked to view"): a PDF opened with
  //   window.open, which does nothing in the iOS web view. It opens in the app's own viewer
  //   (StoredDocViewer, the check-in "View document" one): a fresh link from the stored key.
  const [showDocViewer, setShowDocViewer] = useState(false)
  const caseId = useCaseStore((st) => st.currentCase && st.currentCase.id)
  const isImage = item.type?.startsWith('image/')
  const isVideo = item.type?.startsWith('video/')
  const isPdf = item.type === 'application/pdf' || item.name?.toLowerCase().endsWith('.pdf')
  const displayUrl = item.url || item.storageUrl || item.data
  const displayName = mdName || item.name
  
  const handleTap = () => {
    if (onView) { onView(); return }
    if (isVideo && displayUrl) {
      setShowVideoModal(true)
      return
    }
    if (isImage && displayUrl) {
      setShowImageModal(true)
      return
    }
    // s101 TF66: a PDF opens inside the app; another document type keeps the old open.
    if (storedDocIsPdf(item) && (item.storageUrl || displayUrl)) {
      setShowDocViewer(true)
      return
    }
    if (displayUrl) {
      openDocument(displayUrl, displayName)
    }
  }
  
  return (
    <>
      <div 
        className={`flex items-center gap-3 py-2.5 px-3 rounded-xl bg-white border border-gray-100 ${displayUrl ? 'cursor-pointer active:bg-gray-50' : ''}`}
        onClick={handleTap}
      >
        {/* Thumbnail — 72px */}
        {isImage && displayUrl ? (
          <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
            <img src={displayUrl} alt="" className="w-full h-full object-cover" onError={() => onBroken && onBroken()} />
          </div>
        ) : isVideo && displayUrl ? (
          <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-900 relative">
            <video src={displayUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center">
                <div className="w-0 h-0 ml-0.5 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[10px]" style={{ borderLeftColor: NAVY }} />
              </div>
            </div>
          </div>
        ) : isVideo ? (
          <div className="w-[72px] h-[72px] rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0">
            <Film size={24} className="text-gray-400" />
          </div>
        ) : isPdf ? (
          <div className="w-[72px] h-[72px] rounded-xl flex flex-col items-center justify-center flex-shrink-0 border border-red-100" style={{ backgroundColor: '#FFF5F5' }}>
            <FileText size={24} className="text-red-400" />
            <span className="text-[8px] font-bold text-red-400 mt-0.5">{t('detect.pdf')}</span>
          </div>
        ) : (
          <div className="w-[72px] h-[72px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ICE_BLUE }}>
            <FileText size={24} style={{ color: BLUE }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-gray-800 truncate">{displayName}</p>
          {mdName && mdName !== item.name && <p className="text-[10px] text-gray-400 truncate">{item.name}</p>}
          {item.size && <p className="text-[10px] text-gray-400 mt-0.5">{formatFileSize(item.size)}</p>}
          {displayUrl && (
            <p className="text-[10px] font-medium mt-1" style={{ color: BLUE }}>
              {isVideo ? t('detect.tapToPlay') : isImage ? t('detect.tapToView') : t('detect.tapToView')}
            </p>
          )}
        </div>
        {onRemove && (
          <button onClick={(e) => { e.stopPropagation(); onRemove() }} className="p-1.5 text-gray-300 active:text-red-400">
            <X size={14} />
          </button>
        )}
      </div>
      
      {/* Video playback modal */}
      {showVideoModal && displayUrl && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowVideoModal(false)}
        >
          <button 
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
            onClick={() => setShowVideoModal(false)}
          >
            <X size={20} className="text-white" />
          </button>
          <video 
            src={displayUrl} 
            className="max-w-full max-h-[80vh] rounded-xl" 
            controls 
            autoPlay 
            playsInline
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
      
      {showDocViewer && (
        <StoredDocViewer
          open={showDocViewer}
          doc={{ name: displayName, type: item.type, storagePath: item.storageUrl || null, url: item.url || item.data || null }}
          caseId={caseId}
          title={displayName}
          onClose={() => setShowDocViewer(false)}
          t={t}
        />
      )}

      {/* Image fullscreen modal */}
      {showImageModal && displayUrl && createPortal(
        <div 
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowImageModal(false)}
        >
          <button 
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
            onClick={() => setShowImageModal(false)}
          >
            <X size={20} className="text-white" />
          </button>
          <img 
            src={displayUrl} 
            alt={displayName}
            className="max-w-full max-h-[85vh] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </>
  )
}

// ===== PROD-1198: the files a card's pick added, shown inside that card =====
// The wrapper stops the click, so viewing or removing a file (and its viewer, a portal that still
// bubbles through React) never re-opens the card's file picker.
const CardFiles = ({ entries, busy, onRemove, mdNameOf, onBroken }) => {
  const { t } = useI18n()
  if (!busy && !(entries && entries.length)) return null
  return (
    <div className="px-3 pb-3 space-y-1.5" data-card-files="" onClick={(e) => e.stopPropagation()}>
      {(entries || []).map(({ file, kind }) => (
        <FileRow key={file.id} item={file} mdName={mdNameOf ? mdNameOf(kind, file) : undefined} onRemove={onRemove ? () => onRemove(kind, file) : undefined} onBroken={onBroken} />
      ))}
      {busy && (
        <div className="flex items-center gap-2 px-1 text-[12px]" style={{ color: BLUE }}>
          <OrellaSpinner size={16} tone="inherit" /><span>{t('detect.processingFiles')}</span>
        </div>
      )}
    </div>
  )
}

export default function DetectScreen() {
  const navigate = useNavigate()
  const { currentCase, updateCase, updateAndSaveCase, deleteCase } = useCaseStore()
  const { t, language } = useI18n()
  const { tfa, tq, to } = useQuestionnaireTranslation()
  const [uploads, setUploads] = useState({ photos: [], videos: [], documents: [] })
  const [noFilesToAdd, setNoFilesToAdd] = useState(false)
  const [showAllSuggestions, setShowAllSuggestions] = useState(false) // s126 A2
  const [scaleStep, setScaleStep] = useState(null) // s126 A1: null = decide from the case; 'scale' | 'records' once tapped
  const [scaleDraft, setScaleDraft] = useState('') // s126 A1: the scored English line ScoredScale writes
  const [scaleSaving, setScaleSaving] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [showContent, setShowContent] = useState(() => {
    return !!sessionStorage.getItem(`orella_chat_detect-${currentCase?.id}`)
  })
  const [submitPhase, setSubmitPhase] = useState(null)
  const [uploadProgress, setUploadProgress] = useState({})
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const [dragOverType, setDragOverType] = useState(null) // 'photos' | 'documents' | null
  const initialFileCountRef = useRef(null)
  const detectPrepFallbackFiredRef = useRef(false)
  // ★★ v242.96.0 — the retry has always known when it gave up; nothing could ask.
  //   Eight attempts over ~70 s ran behind a ref, and the three prep zones just
  //   rendered nothing the whole time. This is the flag that lets the screen tell
  //   "coming" from "not coming".
  const [detectPrepExhausted, setDetectPrepExhausted] = useState(false)
  
  const photoInputRef = useRef(null)
  const videoInputRef = useRef(null)
  const documentInputRef = useRef(null)
  // s107: the pinned prior-test row the patient tapped; the files it picks carry its key (document.priorRecord).
  const pendingRecordRef = useRef(null)
  // PROD-1198: the AI suggestion card the patient tapped; the files it picks carry its key (file.suggestion).
  const pendingSuggestionRef = useRef(null)
  // PROD-1198: the card whose pick is uploading right now, so the card itself shows it.
  const [processingCard, setProcessingCard] = useState(null)

  // Detect if case is already shared/claimed
  const caseStatus = currentCase?.status || ''
  const isPostShare = ['shared', 'claimed', 'completed', 'finalized', 'in_follow_up', 'in_observation', 'scheduled'].includes(caseStatus) || 
    !!(currentCase?.isShared || currentCase?.shareCode || currentCase?.share_code || currentCase?.doctorId || currentCase?.doctor_id)

  // v143.0.0: Distinguish first-time Detect visit (show upload zones) from return visits (show file list)
  // After share bridge moved before Detect, isPostShare is true on first visit — but we want the upload zones
  // v154.4.3: If detectComplete but case is no longer shared (user cancelled share), treat as fresh
  const isActuallyShared = !!(currentCase?.status === 'shared' || currentCase?.status === 'claimed' || currentCase?.status === 'in_progress' || currentCase?.status === 'scheduled' || currentCase?.status === 'completed' || currentCase?.status === 'in_follow_up' || currentCase?.status === 'in_observation')
  const hasSubmittedDetect = !!(currentCase?.detectComplete) && isActuallyShared

  // Load existing uploads on mount
  useEffect(() => {
    window.scrollTo(0, 0)
    if (currentCase?.detect?.photos?.length > 0 || currentCase?.detect?.documents?.length > 0 || currentCase?.detect?.videos?.length > 0) {
      const loaded = { photos: currentCase.detect.photos || [], videos: currentCase.detect.videos || [], documents: currentCase.detect.documents || [] }
      setUploads(loaded)
      if (initialFileCountRef.current === null) initialFileCountRef.current = loaded.photos.length + loaded.videos.length + loaded.documents.length
      return
    }
    try {
      const savedData = sessionStorage.getItem(`orella-detect-${currentCase?.id}`)
      if (savedData) {
        const parsed = JSON.parse(savedData)
        const loaded = { photos: parsed.photos || [], videos: parsed.videos || [], documents: parsed.documents || [] }
        setUploads(loaded)
        if (parsed.noFilesToAdd && loaded.photos.length + loaded.videos.length + loaded.documents.length === 0) setNoFilesToAdd(true)
        if (initialFileCountRef.current === null) initialFileCountRef.current = loaded.photos.length + loaded.videos.length + loaded.documents.length
      } else {
        if (initialFileCountRef.current === null) initialFileCountRef.current = 0
      }
    } catch (e) {
      if (initialFileCountRef.current === null) initialFileCountRef.current = 0
    }
  }, [])

  // ★ s99 D477 (Build 61 TestFlight: a "?" thumbnail and a dead "Tap to view"). A stored file's
  //   link is signed for ONE HOUR on read; a case held in the store longer renders dead links.
  //   Read the case once on arrival and once more if a thumbnail fails, and take only the link
  //   fields, merged by file id (utils/freshFileUrls.js).
  const freshReadsRef = useRef(0)
  const refreshFileLinks = async () => {
    if (!currentCase?.id || freshReadsRef.current >= 2) return
    freshReadsRef.current += 1
    try {
      const { casesAPI } = await import('../../services/api')
      const lists = detectListsOf(await casesAPI.get(currentCase.id))
      if (!lists) return
      setUploads(prev => {
        const next = {
          photos: mergeFreshUrls(prev.photos, lists.photos),
          videos: mergeFreshUrls(prev.videos, lists.videos),
          documents: mergeFreshUrls(prev.documents, lists.documents),
        }
        return (next.photos === prev.photos && next.videos === prev.videos && next.documents === prev.documents) ? prev : next
      })
    } catch (e) { /* the links stay as they were; the file itself is safe on the server */ }
  }
  useEffect(() => { refreshFileLinks() }, [currentCase?.id])

  // v142.4.0: Watch for API data arriving after mount (e.g. after deploy/refresh when store rehydrates empty)
  const detectPhotosCount = currentCase?.detect?.photos?.length || 0
  const detectVideosCount = currentCase?.detect?.videos?.length || 0
  const detectDocsCount = currentCase?.detect?.documents?.length || 0
  useEffect(() => {
    const apiTotal = detectPhotosCount + detectVideosCount + detectDocsCount
    const localTotal = uploads.photos.length + uploads.videos.length + uploads.documents.length
    // If API has files but local state is empty, populate from API
    if (apiTotal > 0 && localTotal === 0) {
      console.log('[DetectScreen] API detect data arrived after mount, loading:', { photos: detectPhotosCount, videos: detectVideosCount, docs: detectDocsCount })
      setUploads({
        photos: currentCase.detect.photos || [],
        videos: currentCase.detect.videos || [],
        documents: currentCase.detect.documents || []
      })
      if (initialFileCountRef.current === null || initialFileCountRef.current === 0) {
        initialFileCountRef.current = apiTotal
      }
    }
  }, [detectPhotosCount, detectVideosCount, detectDocsCount])

  // v218.0.2 / fixed v218.0.5: detect-prep fallback with retry-and-backoff.
  //
  // Root cause of the bare-Detect bug: the decoupled call in ViewSummary fires
  // at Submit, but patient_summary is written by the SEPARATE streaming summary
  // path. On a fast fresh-case run, Submit beats the stream's DB write, so
  // /ai/detect-prep reads the case, finds no patient_summary, and returns 409.
  // The old fallback then bailed permanently because it gated on
  // `hasDifferential` — which is also false during the same race — so neither
  // the primary nor the safety net ever recovered. Permanent bare Detect.
  //
  // Fix: gate on "Describe is done so a summary is expected" (describeComplete
  // or a shared/in-progress status), NOT on the differential already being
  // visible client-side. Then retry the endpoint with backoff — 409 means
  // "summary not landed yet, it's coming", not "give up". The stream's write
  // lands within a few seconds; a short retry schedule reliably catches it.
  // Still fully isolated: if every attempt fails, the screen degrades to
  // exactly today's working behavior (standard uploaders functional).
  useEffect(() => {
    if (detectPrepFallbackFiredRef.current) return
    const ps = currentCase?.patientSummary || currentCase?.patient_summary || {}
    if (ps?.detectPrep) return // already have it — nothing to do

    // A summary is EXPECTED if the patient finished Describe (or the case has
    // already advanced to a shared/active status). If Describe isn't complete,
    // there's genuinely nothing to build on yet — don't poll.
    // v242.96.0 — this precondition now lives in ./detectPrepState.js, because the
    // SCREEN has to ask the same question to decide whether to show a skeleton. Two
    // copies of "does this case expect guidance?" would drift, and a skeleton on a
    // case that expects nothing is a lie that never resolves.
    if (!expectsDetectPrep(currentCase)) return

    detectPrepFallbackFiredRef.current = true
    const lang = (typeof language === 'string' && language) || 'en'
    // v220.9.3 — widened backoff schedule to cover the slow v117
    // /ai/patient-summary path (Haiku PVS ~35s + Haiku education ~22s = ~60s
    // before patient_summary lands). Previous schedule maxed at ~30s, so on
    // the non-streaming fallback path the retry could expire BEFORE
    // patient_summary was written, leaving Detect bare. Server-side fixes
    // (PVS fallback in detect-prep + inline detect-prep at end of v117)
    // make this retry largely redundant, but it remains the safety net for
    // older deployed APIs and edge cases. Total ~70s, 8 attempts.
    const delays = [0, 2000, 4000, 7000, 9000, 12000, 15000, 21000]
    let cancelled = false
    const timers = []

    const attempt = (i) => {
      if (cancelled || i >= delays.length) return
      aiAPI.generateDetectPrep(currentCase.id, lang).then(prep => {
        if (cancelled) return
        // ★★★ v242.158.0 — ONE ANSWER TO "DOES THIS PREP CARRY ANYTHING", NOT TWO.
        //   This was a private copy of the same test hasDetectPrepContent makes, and
        //   removing the What-to-Expect card split them: this one still counted
        //   `whatToExpect`, so a prep that returned ONLY exams would satisfy it, STOP THE
        //   RETRY, and leave the patient on a screen that renders nothing and says nothing
        //   is coming. The retry must stop on renderable content, which is the question the
        //   owner already answers.
        const ok = hasDetectPrepContent(prep)
        if (ok) {
          const base = currentCase.patientSummary || currentCase.patient_summary || {}
          const mergedSummary = { ...base, detectPrep: prep }
          try {
            updateCase({ patientSummary: mergedSummary, patient_summary: mergedSummary })
          } catch (_) { /* non-blocking */ }
          return // success — stop retrying
        }
        // Empty/!ok (covers 409 "summary not ready", 502, malformed) → retry
        if (i + 1 < delays.length) {
          timers.push(setTimeout(() => attempt(i + 1), delays[i + 1]))
        } else {
          setDetectPrepExhausted(true)   // ★ the skeleton must terminate
        }
      }).catch(() => {
        if (cancelled) return
        if (i + 1 < delays.length) {
          timers.push(setTimeout(() => attempt(i + 1), delays[i + 1]))
        } else {
          setDetectPrepExhausted(true)
        }
        /* tail-of-tail: every attempt failed → today's behavior, no crash */
      })
    }
    timers.push(setTimeout(() => attempt(0), delays[0]))

    return () => { cancelled = true; timers.forEach(clearTimeout) }
  }, [currentCase?.id])

  // DELETE-CURRENTCASE-NULL-1 — DECLARATIVE REDIRECT, NOT navigate() IN RENDER.
  // Calling navigate() here updates the router WHILE this component is rendering, and
  // React logs exactly that: "Cannot update a component (BrowserRouter) while rendering
  // a different component". That is the React error a patient saw when they deleted a
  // case mid-describe — deleteCase() nulls currentCase, this screen re-rendered on the
  // null and took this arm. The delete and the redirect both completed, which is why it
  // read as cosmetic. <Navigate> is the supported way to say the same thing.
  // FHOOK: the expandedPrep hook (v225.13.0 #3/#4, per-card expand state) is declared here, ABOVE the
  //   early return, so the hook count does not change when currentCase goes null (rules of hooks).
  const [expandedPrep, setExpandedPrep] = useState(() => new Set())
  if (!currentCase) return <Navigate to="/home" replace />

  const MAX_PHOTO_SIZE = 5 * 1024 * 1024
  const MAX_VIDEO_SIZE = 25 * 1024 * 1024
  const MAX_DOC_SIZE = 10 * 1024 * 1024
  const MAX_TOTAL_SIZE = 50 * 1024 * 1024

  // Byte length of a base64 data URL, without materialising it again.
  const dataUrlBytes = (dataUrl) => {
    const i = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1
    if (i < 0) return 0
    const b64 = dataUrl.length - i - 1
    const pad = dataUrl.endsWith('==') ? 2 : dataUrl.endsWith('=') ? 1 : 0
    return Math.max(0, Math.floor(b64 * 3 / 4) - pad)
  }

  const compressImage = (file, maxWidth = 1200, quality = 0.65) => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      const url = URL.createObjectURL(file)
      img.onload = () => {
        try {
          URL.revokeObjectURL(url)
          let { width, height } = img
          if (width > maxWidth) { height = Math.round(height * (maxWidth / width)); width = maxWidth }
          const canvas = document.createElement('canvas')
          canvas.width = width; canvas.height = height
          canvas.getContext('2d').drawImage(img, 0, 0, width, height)
          resolve(canvas.toDataURL('image/jpeg', quality))
        } catch (err) { reject(err) }
      }
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')) }
      img.src = url
    })
  }

  // ★★ v242.90.0 (TF b37 #5) — SAY WHAT WENT WRONG.
  //   Until now every upload failure rendered t('detect.uploadError', { message:
  //   file.name }) — a template whose parameter is named `message` and was handed
  //   a FILENAME. "Error: image.jpg" was all the patient got, and all we got.
  //   uploadToStorageResult now returns the reason; this turns it into a sentence.
  const describeUploadFailure = (res, name) => {
    const s = res?.status
    switch (res?.reason) {
      case 'no-auth':
        return t('detect.uploadFailedSignedOut', 'You have been signed out. Please sign in again, then re-add this file.')
      case 'sign-failed':
        if (s === 401 || s === 403) return t('detect.uploadFailedSignedOut', 'You have been signed out. Please sign in again, then re-add this file.')
        if (s === 413) return t('detect.uploadFailedTooLarge', { name, defaultValue: '"{{name}}" is too large to upload.' })
        return t('detect.uploadFailedServer', { name, status: s, defaultValue: 'Could not upload "{{name}}" — the server refused it ({{status}}).' })
      case 'upload-failed':
        return t('detect.uploadFailedTransfer', { name, status: s, defaultValue: 'Could not upload "{{name}}" — the transfer failed ({{status}}). Check your connection and try again.' })
      case 'invalid-data':
        return t('detect.uploadFailedUnreadable', { name, defaultValue: 'Could not read "{{name}}".' })
      case 'malformed-sign':
        return t('detect.uploadFailedServerResponse', { name, defaultValue: 'Could not upload "{{name}}" — unexpected response from the server.' })
      default:
        return t('detect.uploadFailedGeneric', { name, detail: res?.detail || '', defaultValue: 'Could not upload "{{name}}". {{detail}}' })
    }
  }

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    if (!file) { reject(new Error(t('detect.noFile'))); return }
    const reader = new FileReader()
    const timeout = setTimeout(() => { reader.abort(); reject(new Error('Timeout')) }, 30000)
    reader.onload = () => { clearTimeout(timeout); reader.result ? resolve(reader.result) : reject(new Error('Empty result')) }
    reader.onerror = () => { clearTimeout(timeout); reject(new Error('Read error')) }
    try { reader.readAsDataURL(file) } catch (err) { clearTimeout(timeout); reject(err) }
  })

  const handleFileSelect = async (e, type) => {
    const files = Array.from(e.target.files)
    const recordKey = type === 'documents' ? pendingRecordRef.current : null
    const suggestionKey = pendingSuggestionRef.current
    pendingRecordRef.current = null
    pendingSuggestionRef.current = null
    // PROD-1198: every file of a card's pick carries that card, whatever list it lands in.
    const cardTag = suggestionKey ? { suggestion: suggestionKey } : {}
    if (files.length === 0) return
    setIsProcessing(true)
    setProcessingCard(suggestionKey || (recordKey ? pinnedCardKey(recordKey) : null))
    setUploadError(null)
    try {
      const currentTotalSize = uploads.photos.reduce((s, f) => s + (f.size || 0), 0) + uploads.videos.reduce((s, f) => s + (f.size || 0), 0) + uploads.documents.reduce((s, f) => s + (f.size || 0), 0)
      const processedPhotos = [], processedVideos = [], processedDocs = []
      let newTotalSize = currentTotalSize
      for (const file of files) {
        try {
          const isVideo = file.type?.startsWith('video/')
          const actualType = isVideo ? 'videos' : type
          const maxSize = actualType === 'photos' ? MAX_PHOTO_SIZE : actualType === 'videos' ? MAX_VIDEO_SIZE : MAX_DOC_SIZE
          // ★ v242.90.0 — an image is compressed to ~150-300 KB BEFORE it is uploaded,
          //   so judging the ORIGINAL against MAX_PHOTO_SIZE rejected ordinary 12 MP
          //   iPhone captures for a size they would never have had by upload time.
          //   Images are checked after compression, at the call site; everything else
          //   is checked here, because nothing shrinks it.
          const willCompress = !!file.type?.startsWith('image/') && (actualType === 'photos' || actualType === 'documents')
          if (!willCompress && file.size > maxSize) { setUploadError(`"${file.name}" too large. Max: ${formatFileSize(maxSize)}`); continue }
          if (newTotalSize + file.size > MAX_TOTAL_SIZE) { setUploadError(t('detect.totalLimitExceeded', { size: formatFileSize(MAX_TOTAL_SIZE) })); break }
          const { uploadToStorageResult } = await import('../../services/storage')
          if (actualType === 'videos') {
            const preview = URL.createObjectURL(file)
            const upRes = await uploadToStorageResult(currentCase.id, file.name, file, file.type)
            if (!upRes.ok) { setUploadError(describeUploadFailure(upRes, file.name)); continue }
            const storageUrl = upRes.path
            processedVideos.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: file.type, url: preview, data: null, storageUrl, uploadedAt: new Date().toISOString(), ...cardTag })
            newTotalSize += file.size; continue
          }
          if (actualType === 'photos' && file.type?.startsWith('image/')) {
            const compressed = await compressImage(file, 1200, 0.65)
            if (dataUrlBytes(compressed) > MAX_PHOTO_SIZE) { setUploadError(`"${file.name}" too large. Max: ${formatFileSize(MAX_PHOTO_SIZE)}`); continue }
            const upRes = await uploadToStorageResult(currentCase.id, file.name, compressed, 'image/jpeg')
            if (!upRes.ok) { setUploadError(describeUploadFailure(upRes, file.name)); continue }
            const storageUrl = upRes.path
            processedPhotos.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: 'image/jpeg', data: null, url: compressed, storageUrl, uploadedAt: new Date().toISOString(), ...cardTag })
            newTotalSize += file.size; continue
          }
          if (actualType === 'documents') {
            // PHI: all documents upload to storage; base64 is never persisted. Fail closed.
            if (file.type?.startsWith('image/')) {
              const compressedDoc = await compressImage(file, 1600, 0.72)
              if (dataUrlBytes(compressedDoc) > MAX_DOC_SIZE) { setUploadError(`"${file.name}" too large. Max: ${formatFileSize(MAX_DOC_SIZE)}`); continue }
              const upRes = await uploadToStorageResult(currentCase.id, file.name, compressedDoc, 'image/jpeg')
              if (!upRes.ok) { setUploadError(describeUploadFailure(upRes, file.name)); continue }
              const storageUrl = upRes.path
              processedDocs.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: 'image/jpeg', data: null, url: compressedDoc, storageUrl, uploadedAt: new Date().toISOString(), ...(recordKey ? { priorRecord: recordKey } : {}), ...cardTag })
            } else {
              const preview = URL.createObjectURL(file)
              const upRes = await uploadToStorageResult(currentCase.id, file.name, file, file.type)
              if (!upRes.ok) { setUploadError(describeUploadFailure(upRes, file.name)); continue }
              const storageUrl = upRes.path
              processedDocs.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: file.type, data: null, url: preview, storageUrl, uploadedAt: new Date().toISOString(), ...(recordKey ? { priorRecord: recordKey } : {}), ...cardTag })
            }
          } else {
            const preview = URL.createObjectURL(file)
            const upRes = await uploadToStorageResult(currentCase.id, file.name, file, file.type)
            if (!upRes.ok) { setUploadError(describeUploadFailure(upRes, file.name)); continue }
            const storageUrl = upRes.path
            processedPhotos.push({ id: Date.now() + Math.random(), name: file.name, size: file.size, type: file.type || 'application/octet-stream', data: null, url: preview, storageUrl, uploadedAt: new Date().toISOString(), ...cardTag })
          }
          newTotalSize += file.size
        } catch (fileError) { setUploadError(t('detect.fileError', { name: file.name, message: fileError.message || 'Unknown' })); continue }
      }
      if (processedPhotos.length > 0 || processedVideos.length > 0 || processedDocs.length > 0) {
        setUploads(prev => ({ ...prev, photos: [...prev.photos, ...processedPhotos], videos: [...prev.videos, ...processedVideos], documents: [...prev.documents, ...processedDocs] }))
        setNoFilesToAdd(false)
      }
    } catch (error) { setUploadError(t('detect.uploadError', { message: error.message || t('common.pleaseTryAgain') })) }
    finally { setIsProcessing(false); setProcessingCard(null); if (e.target?.value !== undefined) e.target.value = '' }
  }

  // PROD-1199: a removal leaves "Nothing to add" unticked; it comes back only once no file is left.
  const removeFile = (type, fileId) => { setUploads(prev => ({ ...prev, [type]: prev[type].filter(f => f.id !== fileId) })); setNoFilesToAdd(false) }

  // PROD-1198: one way into each file input. A card passes its key; the plain tiles pass none, which
  //   also clears a key left behind by a card pick the patient cancelled.
  const pickPhotos = (cardKey = null) => { pendingRecordRef.current = null; pendingSuggestionRef.current = cardKey; photoInputRef.current?.click() }
  const pickDocuments = (cardKey = null) => { pendingRecordRef.current = null; pendingSuggestionRef.current = cardKey; documentInputRef.current?.click() }

  // Drag-and-drop handlers
  const handleDragOver = (e, type) => { e.preventDefault(); e.stopPropagation(); setDragOverType(type) }
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOverType(null) }
  const handleDrop = (e, type) => {
    e.preventDefault(); e.stopPropagation(); setDragOverType(null)
    pendingRecordRef.current = null; pendingSuggestionRef.current = null // a drop is never a card's pick
    const files = e.dataTransfer?.files
    if (files?.length > 0) handleFileSelect({ target: { files, value: '' } }, type)
  }

  const totalFiles = uploads.photos.length + uploads.videos.length + uploads.documents.length
  // PROD-1199: "Nothing to add" counts only while there is nothing added.
  const nothingToAdd = noFilesToAdd && showNothingToAdd(uploads)
  const hasContent = totalFiles > 0 || nothingToAdd
  const hasNewFiles = totalFiles > (initialFileCountRef.current || 0)

  // ============ DIRECT API SYNC ============
  const syncDetectFilesToAPI = async () => {
    const { casesAPI } = await import('../../services/api')
    const caseId = currentCase.id
    const allFileIds = [...uploads.photos, ...uploads.videos, ...uploads.documents].reduce((acc, f) => { acc[f.id] = 'uploading'; return acc }, {})
    setUploadProgress(allFileIds)
    const photosPayload = uploads.photos.map(p => ({ id: p.id, name: p.name, type: p.type, size: p.size, data: null, url: null, storageUrl: p.storageUrl || null, uploadedAt: p.uploadedAt, hasData: !!p.storageUrl, ...(p.suggestion ? { suggestion: p.suggestion } : {}) }))
    const videosPayload = uploads.videos.map(v => ({ id: v.id, name: v.name, type: v.type, size: v.size, data: null, url: null, storageUrl: v.storageUrl || null, uploadedAt: v.uploadedAt, hasData: !!v.storageUrl, ...(v.suggestion ? { suggestion: v.suggestion } : {}) }))
    const documentsPayload = uploads.documents.map(d => ({ id: d.id, name: d.name, type: d.type, size: d.size, data: null, url: null, storageUrl: d.storageUrl || null, uploadedAt: d.uploadedAt, hasData: !!d.storageUrl, ...(d.priorRecord ? { priorRecord: d.priorRecord } : {}), ...(d.suggestion ? { suggestion: d.suggestion } : {}) }))
    const fullPayload = { photos: photosPayload, videos: videosPayload, documents: documentsPayload }
    let payloadStr
    try { payloadStr = JSON.stringify(fullPayload) } catch (e) {
      await casesAPI.update(caseId, { photos: photosPayload.map(({ data, url, ...m }) => ({ ...m, hasData: false })), videos: videosPayload, documents: documentsPayload.map(({ data, url, ...m }) => ({ ...m, hasData: false })) })
      setUploadProgress(prev => { const u = { ...prev }; Object.keys(u).forEach(id => { u[id] = 'failed' }); return u })
      return true
    }
    const sizeMB = payloadStr.length / (1024 * 1024)
    if (sizeMB < 3.5) {
      try {
        await casesAPI.update(caseId, fullPayload)
        setUploadProgress(prev => { const u = { ...prev }; Object.keys(u).forEach(id => { u[id] = 'done' }); return u })
        return true
      } catch (err) { /* fall through */ }
    }
    // Phased
    try {
      await casesAPI.update(caseId, { photos: photosPayload.map(({ data, url, ...meta }) => meta), videos: videosPayload, documents: documentsPayload.map(({ data, url, ...meta }) => meta) })
      setUploadProgress(prev => { const u = { ...prev }; uploads.videos.forEach(v => { if (v.storageUrl) u[v.id] = 'done' }); return u })
    } catch (err) {
      setUploadProgress(prev => { const u = { ...prev }; Object.keys(u).forEach(id => { u[id] = 'failed' }); return u })
      return false
    }
    for (let i = 0; i < photosPayload.length; i++) {
      const photo = photosPayload[i]
      if (!photo.data) { setUploadProgress(prev => ({ ...prev, [photo.id]: 'done' })); continue }
      try {
        await casesAPI.update(caseId, { photos: photosPayload.map((p, idx) => idx === i ? p : { id: p.id, name: p.name, type: p.type, size: p.size, uploadedAt: p.uploadedAt, hasData: p.hasData }) })
        setUploadProgress(prev => ({ ...prev, [photo.id]: 'done' }))
      } catch (err) { setUploadProgress(prev => ({ ...prev, [photo.id]: 'failed' })) }
    }
    if (documentsPayload.filter(d => d.data).length > 0) {
      try { await casesAPI.update(caseId, { documents: documentsPayload }); uploads.documents.forEach(d => setUploadProgress(prev => ({ ...prev, [d.id]: 'done' }))) }
      catch (err) { uploads.documents.forEach(d => setUploadProgress(prev => ({ ...prev, [d.id]: 'failed' }))) }
    } else { uploads.documents.forEach(d => setUploadProgress(prev => ({ ...prev, [d.id]: 'done' }))) }
    return true
  }

  const handleRetryFailed = async () => { try { await syncDetectFilesToAPI() } catch (err) {} }

  // Pre-share submit → Share
  const handleSubmitDetect = async () => {
    setSubmitPhase('processing')
    try {
      // s126 A2: Send is always live — sending with no file IS "nothing to add" (the old checkbox card is gone).
      const detectData = detectSaveData(uploads, noFilesToAdd || (uploads.photos.length + uploads.videos.length + uploads.documents.length) === 0) // PROD-1199: never files AND noFilesToAdd
      try { sessionStorage.setItem(`orella-detect-${currentCase.id}`, JSON.stringify(detectData)) } catch (e) {}
      updateCase({ detect: detectData, hasUploads: totalFiles > 0, detectComplete: true })
      if (totalFiles > 0) { try { await syncDetectFilesToAPI() } catch (err) {} }
      await new Promise(r => setTimeout(r, 1200))
      setSubmitPhase('success')
      await new Promise(r => setTimeout(r, 800))
      setSubmitPhase('exit')
      await new Promise(r => setTimeout(r, 500))
      // ★ s98: Describe → Detect → share. A case not yet sent goes on to the share hub (D468);
      //   a sent one to Diagnose as before.
      if (caseIsShared(currentCase)) navigate('/case/diagnose') // PROD-654: a push, back returns to Detect
      else navigate('/case/share-hub')
    } catch (err) { setSubmitPhase(null) }
  }

  // Post-share save → CaseHome
  const handleSaveNewFiles = async () => {
    setSubmitPhase('processing')
    try {
      const detectData = detectSaveData(uploads, noFilesToAdd) // PROD-1199: never files AND noFilesToAdd
      try { sessionStorage.setItem(`orella-detect-${currentCase.id}`, JSON.stringify(detectData)) } catch (e) {}
      updateCase({ detect: detectData, hasUploads: totalFiles > 0, detectComplete: true })
      try { await syncDetectFilesToAPI() } catch (err) {}
      await new Promise(r => setTimeout(r, 1000))
      setSubmitPhase('success')
      await new Promise(r => setTimeout(r, 600))
      setSubmitPhase('exit')
      await new Promise(r => setTimeout(r, 400))
      navigate('/case/home', { replace: true })
    } catch (err) { setSubmitPhase(null) }
  }

  const handleSkip = () => {
    const detectData = { photos: [], videos: [], documents: [], noFilesToAdd: true, completed: true }
    try { sessionStorage.setItem(`orella-detect-${currentCase.id}`, JSON.stringify(detectData)) } catch (e) {}
    updateAndSaveCase({ detect: detectData, hasUploads: false, detectComplete: true })
    // ★ s98: same rule as submit — not sent yet → the share step; sent → Diagnose.
    if (caseIsShared(currentCase)) navigate('/case/diagnose') // PROD-654: a push, back returns to Detect
    else navigate('/case/share-hub')
  }

  const handleDeleteCase = async () => {
    // DELETE-CURRENTCASE-NULL-1 — NAVIGATE FIRST, DELETE SECOND.
    // deleteCase() sets currentCase to null in the store. Awaiting it here re-rendered
    // THIS screen against that null while it was still mounted, and the first thing in
    // render that reads currentCase.<x> without a ?. threw a React error. The delete and
    // the navigation both completed, which is exactly why it looked cosmetic: the app
    // "worked" and threw anyway. Unmounting first removes the whole class, at every one
    // of the six screens that can delete their own case, rather than optional-chaining
    // whichever line happened to fire (RULES §1bv(a)).
    const id = currentCase?.id
    setShowDeleteConfirm(false)
    navigate('/home', { replace: true })
    // ★ PROD-416: a delete the server refuses leaves the case, and the patient is told.
    if (id) { try { await deleteCase(id) } catch (err) { reportDeleteFailure(t, err) } }
  }
  const handleSaveAsDraft = async () => { try { await updateAndSaveCase({ status: 'draft' }); navigate('/home', { replace: true }) } catch (err) {} }

  const caseInfo = `${formatCaseSpecialty(currentCase, t)} › ${currentCase.focusAreaName ? tfa(currentCase.focusAreaName) : ''}`
  // ── s126 A1 (canvas "Orella Diagnostic Scales"; copy ORELLA_SCALES_COPY_v1 APPROVED 2026-10-31) ──────────────
  //   The top HIGH diagnosis with a mapped FREE scale gets one scale, before the records. Optional (Skip). Saved to
  //   detect_data.patientScales [{scale, answer, dx, at, skipped}]; the API's records refresh re-ranks the shared
  //   differential with it (v243.405.0). Never shown once the case is shared (hasSubmittedDetect).
  const scaleOffer = pickScaleOffer((currentCase?.patientSummary || currentCase?.patient_summary || {}).differentialDx, scalesLib)
  const savedScales = savedPatientScales(currentCase)
  const savedForOffer = scaleOffer ? savedScales.find((r) => r && r.scale === scaleOffer.scale.id) : null
  const scaleIsNewOffer = !!scaleOffer && !savedForOffer && savedScales.some((r) => r && !r.skipped)
  const showScaleStep = !!scaleOffer && !hasSubmittedDetect && (scaleStep === 'scale' || (scaleStep === null && !savedForOffer))
  const tScale = scaleText(scalesLib, language)
  const scaleValue = scaleDraft || (savedForOffer && !savedForOffer.skipped ? savedForOffer.answer : '') || ''
  const scaleScores = scaleOffer ? parseScale(scaleValue, scaleOffer.scale) : []
  const scaleDone = scaleScores.length > 0 && scaleScores.every((v) => v !== null)
  const saveScale = async (skipped) => {
    if (!scaleOffer || scaleSaving) return
    setScaleSaving(true)
    const row = { scale: scaleOffer.scale.id, dx: scaleOffer.dx, at: new Date().toISOString(),
      ...(skipped ? { skipped: true } : { answer: scaleValue }) }
    const rows = [...savedScales.filter((r) => r && r.scale !== row.scale), row]
    updateCase({ detectData: { ...(currentCase?.detectData || {}), patientScales: rows }, patientScales: rows })
    try {
      if (currentCase?.id && !String(currentCase.id).startsWith('local-')) {
        const { casesAPI } = await import('../../services/api')
        await casesAPI.update(currentCase.id, { detectData: { patientScales: rows } })
      }
    } catch (e) { /* kept on the phone; the next Detect save carries the case again */ }
    setScaleSaving(false)
    setScaleDraft('')
    setScaleStep('records')
  }
  const savedScaleLine = savedForOffer && !savedForOffer.skipped && scaleOffer
    ? `${scaleOffer.scale.scale} ${scaleTotalText(scaleOffer.scale, scaleScore(scaleOffer.scale, parseScale(savedForOffer.answer, scaleOffer.scale)))}`
    : ''

  const chatMessage = showScaleStep
    ? (scaleIsNewOffer ? t('detect.scaleNewOffer', "Your summary changed, so a different score fits better now. Your earlier answers stay on your case.")
      : t('detect.scaleBubble', 'One quick score for your doctor. Doctors use it for symptoms like yours, and it takes about a minute.'))
    : hasSubmittedDetect
    ? "Your evidence is shared with your doctor. Add anything new here — they'll see it right away."
    : nothingToAdd
      ? (t('detect.chatNoFiles') || "No problem — you can always add files later. Continue to Diagnose when you're ready.")
      : t('detect.chatSimple', 'Any records or photos for your doctor? Tap what you have.') // s126 A2

  const doctorFindings = currentCase?.detectData?.clinicalUploads || currentCase?.detect?.doctorFindings || []
  const hasDoctorFindings = Array.isArray(doctorFindings) && doctorFindings.length > 0

  // v142.5.0: MD-assigned names for patient media (synced from MD panel detectData)
  const mdNames = currentCase?.detectData?.patientMediaNames || {}

  // v160: Exam preview data from patient_summary (generated during Describe SSE stream)
  const patientSummary = currentCase?.patientSummary || currentCase?.patient_summary || {}

  // v218.0.3 Stage 2: native three-zone consumption of patient_summary.detectPrep
  // (generated by the decoupled /ai/detect-prep endpoint, built on the committed
  // differential). No shim — each group renders in its own zone.
  //   capturable   → Zone 1 "Suggested for your case" (1-2 patient capture cards)
  //   priorRecords → Zone 2 "Prior records that would help"
  //   whatToExpect → ★ v242.158.0: ZONE 3 REMOVED (TestFlight 2026-09-06). The field is
  //                  still generated and stored; it simply has no surface here now.
  const detectPrep = patientSummary?.detectPrep || null
  const prepCapturable = Array.isArray(detectPrep?.capturable) ? detectPrep.capturable.filter(c => c && c.title) : []
  const prepPriorRecords = Array.isArray(detectPrep?.priorRecords) ? detectPrep.priorRecords.filter(r => r && r.label) : []
  // ★ s107 (catalog 3.8, Dr. Rami 2026-10-16): the records the patient SAID they have are pinned first,
  //   from the answers themselves — they show even when the AI prep is still coming or suggests nothing.
  //   utils/priorTests.js owns the rule; a row is Added once a file attached through it is in the uploads.
  const recordFocusArea = useQuestionnaireStore.getState().getFocusArea(currentCase?.specialtyId, currentCase?.focusAreaId)
  const pinnedRecords = priorRecordRows(recordFocusArea, currentCase?.answers || {})
  const addedRecords = addedRecordKeys(uploads.documents)
  const attachRecord = (key) => { pendingSuggestionRef.current = null; pendingRecordRef.current = key; documentInputRef.current?.click() }

  // PROD-1198: the cards on screen own the files picked through them; the general list shows the rest.
  const shownCaptures = prepCapturable.slice(0, 2)
  const shownPrepRecords = prepPriorRecords.slice(0, 4)
  const cardKeys = [
    ...shownCaptures.map(cap => captureCardKey(cap.label || cap.title)),
    ...pinnedRecords.map(row => pinnedCardKey(row.key)),
    ...shownPrepRecords.map(rec => recordCardKey(rec.label)),
  ]
  const { byCard, general } = splitUploads(uploads, cardKeys)
  // s126 A2: at most 3 suggestion cards on first view ("You mentioned this" rows always show; a card holding a file
  //   is never hidden). The card keys above still come from the full lists, so no file changes owner when a card hides.
  const sugg = budgetSuggestions({
    pinned: pinnedRecords, captures: shownCaptures, records: shownPrepRecords, showAll: showAllSuggestions,
    captureHasFiles: (cap) => (byCard.get(captureCardKey(cap.label || cap.title)) || []).length > 0,
    recordHasFiles: (rec) => (byCard.get(recordCardKey(rec.label)) || []).length > 0,
  })
  const canCollapseSuggestions = (pinnedRecords.length + shownCaptures.length + shownPrepRecords.length) > 3
  const visibleCaptures = sugg.captures
  const visibleRecords = sugg.records
  // Once the case is shared the general list offers no remove (those files are with the doctor); a card does the same.
  const removeFromCard = hasSubmittedDetect ? null : (kind, file) => removeFile(kind, file.id)
  const inGeneral = new Set([...general.photos, ...general.videos, ...general.documents])
  const generalCount = inGeneral.size
  const MD_PREFIX = { photos: 'photo', videos: 'video', documents: 'document' }
  const mdNameOf = (kind, file) => mdNames[`${MD_PREFIX[kind]}_${uploads[kind].indexOf(file)}`]

  // ★★★ v242.96.0 — READY vs COMING vs NOT COMING. An empty array meant "not here",
  //   which for the first ~60 s of a case's life is the opposite of the truth.
  const prepPhase = detectPrepPhase({
    prep: detectPrep,
    expects: expectsDetectPrep(currentCase),
    retriesExhausted: detectPrepExhausted,
    submitted: !!hasSubmittedDetect,
  })
  const prepPending = prepPhase === 'pending'
  // v160: Show exam preview pre-finalize, doctor findings post-finalize
  const isFinalized = currentCase?.status === 'completed' || currentCase?.status === 'finalized'
  // v225.13.0 (#3/#4): per-card expand state for the concise suggestion/record cards.
  const togglePrep = (key) => setExpandedPrep(prev => {
    const next = new Set(prev)
    next.has(key) ? next.delete(key) : next.add(key)
    return next
  })

  // ===== RENDER =====
  // v242.91.0 (TF b37 #0) — the idle branch's `overflow-auto` was a scroll
  // container that never scrolled (`min-h-full` gives no definite height), so any
  // position:sticky inside it would have anchored to a box that does not move.
  // `.scroll-wrapper` is this app's one scroller (index.css:45). The submitPhase
  // branch keeps `h-full overflow-hidden` ON PURPOSE — that locks the page behind
  // SubmitOverlay, which is scroll OWNERSHIP, not a second scroller, and it says
  // so in data-owns-scroll rather than being silently exempt.
  return (
    <div className={`bg-white flex flex-col relative ${submitPhase ? 'h-full overflow-hidden' : 'min-h-full'}`}>
      {/* PROD-1194 part C (PROD-1198 screenshot): the iOS status bar (clock, wifi) was drawn over the
          scrolled cards. MobileLayout's white top inset is padding that scrolls away with the page, so
          once the patient scrolls nothing sits under the status bar. /case/detect now fills its own
          top inset (MobileLayout NAVY_HEADER_ROUTES): this white band, env(safe-area-inset-top) tall,
          sticks at the top of the scroller and the content scrolls under it. 0 px in a browser.
          Held by check-viewport-bottom-actions A12. */}
      <div aria-hidden="true" data-status-band className="sticky top-0 z-30 bg-white flex-shrink-0" style={{ height: 'env(safe-area-inset-top, 0px)' }} />
      <ChatKeyframes />
      {/* v230.2.4 — accept now includes image/* on the Documents input so the
          device's native "Take Photo or Video" option appears, letting patients
          photograph a paper record (lab result, referral, audiogram). A
          photographed file is bucketed into `documents` by handleFileSelect.
          HEIC/HEIF added for iOS-capture robustness. */}
      <input type="file" ref={photoInputRef} onChange={(e) => handleFileSelect(e, 'photos')} accept="image/*,video/*,.heic,.heif" multiple className="hidden" />
      <input type="file" ref={videoInputRef} onChange={(e) => handleFileSelect(e, 'videos')} accept="video/*" multiple className="hidden" />
      <input type="file" ref={documentInputRef} onChange={(e) => handleFileSelect(e, 'documents')} accept=".pdf,.doc,.docx,.txt,image/*,.heic,.heif" multiple className="hidden" />

      <CompactHeader stepNum={2} totalSteps={3} stepLabel="Detect" caseInfo={caseInfo} onBack={() => caseBack(navigate)} onNotification={() => navigate('/notifications')} />
      <OrellaChatBubble message={chatMessage} animationKey={`detect-${currentCase?.id}`} onComplete={() => setShowContent(true)} />

      {/* ===== CONTENT ===== */}
      <div className="flex-1 px-4 pt-2 pb-4 overflow-auto" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>

        {/* s126 A1: Score · Records · Send, only when this case has a scale to offer. */}
        {scaleOffer && !hasSubmittedDetect && (
          <div className="flex gap-1.5 mb-3" data-detect-steps>
            {[['scale', t('detect.stepScore', 'Score')], ['records', t('detect.stepRecords', 'Records')], ['send', t('detect.stepSend', 'Send')]].map(([k, label], i) => {
              const on = showScaleStep ? k === 'scale' : k === 'records'
              const done = !showScaleStep && k === 'scale'
              return (
                <span key={k} className="flex-1 text-center text-[11px] font-bold rounded-lg py-1.5"
                      style={on ? { background: NAVY, color: '#fff' } : done ? { background: '#D5EFD3', color: '#16A34A' } : { background: '#EEF3F7', color: '#7C93A6' }}>
                  {done ? '✓ ' : `${i + 1} · `}{label}
                </span>
              )
            })}
          </div>
        )}

        {showScaleStep ? (
          <div className="rounded-2xl border p-3.5 mb-2" data-detect-scale={scaleOffer.scale.id} style={{ borderColor: '#E1EAF1' }}>
            <p className="text-[10px] font-extrabold tracking-[0.1em] uppercase mb-1" style={{ color: BLUE }}>{tScale(scaleOffer.scale.title)}</p>
            <ScoredScale question={scaleOffer.scale} value={scaleValue} onChange={setScaleDraft} disabled={scaleSaving} tq={tScale} to={tScale} />
            {language === 'es' && <p className="text-[10px] text-gray-400 mt-1">{tScale("Orella's translation.")}</p>}
          </div>
        ) : (<>
        {/* s126 A1: the scale this case already carries. */}
        {savedScaleLine && !hasSubmittedDetect && (
          <div className="flex items-center gap-3 rounded-2xl border p-3 mb-3" data-detect-scale-done style={{ borderColor: '#E1EAF1' }}>
            <span className="w-8 h-8 rounded-full grid place-items-center flex-shrink-0 font-extrabold" style={{ background: '#D5EFD3', color: '#16A34A' }}>✓</span>
            <span className="min-w-0 flex-1">
              <b className="block text-[13.5px]" style={{ color: NAVY }}>{t('detect.scaleAdded', { defaultValue: '{{scale}} {{score}} · added to your case', scale: scaleOffer.scale.scale, score: savedScaleLine.split(' ').slice(1).join(' ') })}</b>
              <span className="text-[11.5px]" style={{ color: '#5A7185' }}>{t('detect.scaleAddedSub', 'Your doctor sees each answer.')} </span>
              <button type="button" onClick={() => setScaleStep('scale')} className="text-[11.5px] font-semibold underline" style={{ color: BLUE }}>{t('detect.scaleChange', 'Change answers')}</button>
            </span>
          </div>
        )}

        {isProcessing && (
          <div className="flex items-center gap-2 mb-3" style={{ color: BLUE }}>
            <OrellaSpinner size={16} tone="inherit" /><span className="text-sm">{t('detect.processingFiles')}</span>
          </div>
        )}
        {uploadError && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-3 py-2.5 rounded-xl mb-3">
            <AlertCircle size={16} className="flex-shrink-0" /><span className="text-[13px] flex-1">{uploadError}</span>
            <button onClick={() => setUploadError(null)}><X size={14} /></button>
          </div>
        )}

        {/* Upload Progress */}
        {Object.keys(uploadProgress).length > 0 && (
          <div className="mb-3 p-3 rounded-xl border" style={{ backgroundColor: ICE_BLUE, borderColor: LIGHT_BLUE }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] font-semibold" style={{ color: NAVY }}>{t('detect.uploadProgress')}</span>
              {Object.values(uploadProgress).some(s => s === 'failed') && (
                <button onClick={handleRetryFailed} className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: BLUE }}><RefreshCw size={12} /> {t('common.retry', 'Retry')}</button>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {Object.entries(uploadProgress).map(([id, status]) => (
                <div key={id} className={`h-1.5 flex-1 rounded-full transition-all ${status === 'done' ? 'bg-green-400' : status === 'failed' ? 'bg-red-400' : 'animate-pulse'}`} style={status === 'uploading' ? { backgroundColor: BLUE } : undefined} />
              ))}
            </div>
            <p className="text-[11px] text-gray-500 mt-1.5">
              {Object.values(uploadProgress).filter(s => s === 'done').length}/{Object.keys(uploadProgress).length} synced
              {Object.values(uploadProgress).some(s => s === 'failed') && <span className="text-red-500 ml-1">· {Object.values(uploadProgress).filter(s => s === 'failed').length} failed</span>}
            </p>
          </div>
        )}

        {/* ===== v218.0.3 Stage 2 — ZONE 1: Suggested for your case ===== */}
        {/* ★★★ v242.96.0 — DETECT GUIDANCE, LOADING.
            Dr. Rami: "the detect content was not ready by the time I arrived...
            can it persist in a loading state until the content loads?"
            The retry has run for ~70 s since v220.9.3; what was missing was any
            sign of it on screen. These stand in for zones 1 and 3 and are replaced
            in place the moment `detectPrep` lands — same widths, same rhythm, so
            nothing jumps. `orellaSkelPulse` is the app's existing skeleton
            animation (ViewSummary uses it while the differential streams).
            ★ It TERMINATES: `prepPending` goes false when the retry gives up, and
              the screen falls back to the plain uploaders exactly as before. */}
        {prepPending && (
          <div className="mb-4" aria-live="polite" aria-busy="true">
            <div className="flex items-center gap-2 mb-2.5 px-1">
              <OrellaSpinner size={16} />
              <p className="text-[12px] font-semibold" style={{ color: '#5A6B7B' }}>
                {t('detect.preparingGuidance', 'Preparing guidance for your case\u2026')}
              </p>
            </div>
            <div style={{ animation: 'orellaSkelPulse 1.8s ease-in-out infinite' }} aria-hidden="true">
              {[0, 1].map((i) => (
                <div key={i} className="rounded-2xl border p-3 mb-2.5 flex items-center gap-3"
                     style={{ borderColor: '#DCEBFB', backgroundColor: '#F8FBFF' }}>
                  <div className="w-9 h-9 rounded-xl flex-shrink-0" style={{ background: '#E8F1FB' }} />
                  <div className="flex-1 min-w-0">
                    <div className="h-[11px] rounded-md mb-2" style={{ background: '#E8F1FB', width: i === 0 ? '62%' : '48%' }} />
                    <div className="h-[9px] rounded-md" style={{ background: '#EEF4FA', width: '38%' }} />
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border p-3.5 flex items-center gap-3"
                   style={{ borderColor: '#DCEBFB', borderLeft: `4px solid ${LIGHT_BLUE}`, backgroundColor: '#F8FBFF' }}>
                <div className="w-[38px] h-[38px] rounded-xl flex-shrink-0" style={{ background: '#E8F1FB' }} />
                <div className="flex-1 min-w-0">
                  <div className="h-[12px] rounded-md mb-2" style={{ background: '#E8F1FB', width: '68%' }} />
                  <div className="h-[9px] rounded-md" style={{ background: '#EEF4FA', width: '52%' }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1-2 patient-capturable suggestions. Navy card structure, blue capture
            button (variant a). Each card opens the SAME photo input as the standard
            tile, but through pickPhotos(cardKey): PROD-1198, the files it picks carry
            the card (file.suggestion) and render inside it, not in the general list. */}
        {visibleCaptures.length > 0 && (
          <div className="mb-4">
            <SectionHeader icon={<Camera size={13} style={{ color: BLUE }} />} title={t('detect.suggestedForCase') || 'Suggested for your case'} color={NAVY} />
            <div className="space-y-2.5">
              {visibleCaptures.map((cap, i) => {
                const expanded = expandedPrep.has(`cap_${i}`)
                const label = cap.label || cap.title
                const hasFull = cap.title && cap.label && cap.title !== cap.label
                const cardKey = captureCardKey(label)
                const cardFiles = byCard.get(cardKey) || []
                const added = cardFiles.length > 0
                return (
                  <div
                    key={i}
                    onClick={() => pickPhotos(cardKey)}
                    className="rounded-2xl border cursor-pointer transition-colors active:opacity-90"
                    style={added ? { borderColor: '#BFE6C3', backgroundColor: '#F2FAF3' } : { borderColor: '#DCEBFB', backgroundColor: '#F3F9FF' }}
                  >
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-white" style={{ border: `1px solid ${added ? '#BFE6C3' : '#DCEBFB'}` }}>
                        {added ? <Check size={17} style={{ color: '#2F7A38' }} /> : <Camera size={17} style={{ color: BLUE }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold leading-tight" style={{ color: NAVY }}>{label}</p>
                        <p className="flex items-center gap-1 text-[11px] font-medium mt-0.5" style={{ color: added ? '#2F7A38' : BLUE }}>
                          {added ? <><Check size={12} />{t('detect.priorTestAdded')}</> : <><Camera size={12} />{t('detect.tapToAddMedia') || 'Tap to add a photo or video'}</>}
                        </p>
                      </div>
                      {(cap.why || hasFull) && (
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePrep(`cap_${i}`) }}
                          aria-label={t('detect.whyThisHelps') || 'Why this helps'}
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ border: `1px solid ${expanded ? LIGHT_BLUE : '#CBDCEB'}`, backgroundColor: expanded ? ICE_BLUE : '#fff' }}
                        >
                          <Plus size={15} style={{ color: BLUE, transform: expanded ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }} />
                        </button>
                      )}
                    </div>
                    {expanded && (cap.why || hasFull) && (
                      <div className="pr-3 pb-3" style={{ paddingLeft: 60 }}>
                        {hasFull && <p className="text-[11.5px] font-semibold leading-snug mb-1" style={{ color: NAVY }}>{cap.title}</p>}
                        {cap.why && <p className="text-[11.5px] leading-relaxed" style={{ color: '#5A6B7B' }}>{cap.why}</p>}
                      </div>
                    )}
                    <CardFiles entries={cardFiles} busy={processingCard === cardKey} onRemove={removeFromCard} mdNameOf={mdNameOf} onBroken={refreshFileLinks} />
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ===== v218.0.3 Stage 2 — ZONE 2: Prior records that would help ===== */}
        {/* Tappable rows open the documents input — same pipe as the Documents tile, tagged
            with the row (pinned: priorRecord; AI: suggestion, PROD-1198) and shown in it. */}
        {(pinnedRecords.length > 0 || visibleRecords.length > 0) && (
          <div className="mb-4">
            <SectionHeader icon={<FileText size={13} style={{ color: BLUE }} />} title={t('detect.priorRecordsHelp') || 'Prior records that would help'} color={NAVY} />
            <div className="space-y-2.5">
              {pinnedRecords.map((row) => {
                const added = addedRecords.has(row.key)
                const expanded = expandedPrep.has(`pin_${row.key}`)
                return (
                  <div
                    key={`pin_${row.key}`}
                    onClick={() => attachRecord(row.key)}
                    className="rounded-2xl cursor-pointer transition-colors active:opacity-90"
                    style={{ border: `2px solid ${added ? '#BFE6C3' : BLUE}`, backgroundColor: added ? '#F2FAF3' : '#F3F9FF' }}
                  >
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: added ? '#E3F4E5' : ICE_BLUE }}>
                        {added ? <Check size={17} style={{ color: '#2F7A38' }} /> : <FileText size={16} style={{ color: BLUE }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-[13.5px] font-bold leading-tight" style={{ color: NAVY }}>{row.kind === 'option' ? to(row.label) : tq(row.label)}</p>
                          <span className="text-[10px] font-bold px-[7px] py-[2px] rounded-full text-white" style={{ backgroundColor: NAVY }}>{t('detect.youMentionedThis')}</span>
                        </div>
                        <p className="flex items-center gap-1 text-[11px] font-medium mt-0.5" style={{ color: added ? '#2F7A38' : BLUE }}>
                          {added ? <><Check size={11} />{t('detect.priorTestAdded')}</> : <><Upload size={11} />{t('detect.tapToAttachPhotoPdf')}</>}
                        </p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); togglePrep(`pin_${row.key}`) }}
                        aria-label={t('detect.whyThisHelps') || 'Why this helps'}
                        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ border: `1px solid ${expanded ? LIGHT_BLUE : '#CBDCEB'}`, backgroundColor: expanded ? ICE_BLUE : '#fff' }}
                      >
                        <Plus size={15} style={{ color: BLUE, transform: expanded ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }} />
                      </button>
                    </div>
                    {expanded && (
                      <div className="pr-3 pb-3" style={{ paddingLeft: 60 }}>
                        <p className="text-[11.5px] leading-relaxed" style={{ color: '#5A6B7B' }}>{t('detect.priorTestWhy')}</p>
                      </div>
                    )}
                    <CardFiles entries={byCard.get(pinnedCardKey(row.key))} busy={processingCard === pinnedCardKey(row.key)} onRemove={removeFromCard} mdNameOf={mdNameOf} onBroken={refreshFileLinks} />
                  </div>
                )
              })}
              {visibleRecords.map((rec, i) => {
                const expanded = expandedPrep.has(`rec_${i}`)
                const cardKey = recordCardKey(rec.label)
                const cardFiles = byCard.get(cardKey) || []
                const added = cardFiles.length > 0
                return (
                  <div
                    key={i}
                    onClick={() => pickDocuments(cardKey)}
                    className="rounded-2xl border cursor-pointer transition-colors active:opacity-90"
                    style={added ? { borderColor: '#BFE6C3', backgroundColor: '#F2FAF3' } : { borderColor: '#E8EEF4', backgroundColor: '#FAFCFE' }}
                  >
                    <div className="flex items-center gap-3 p-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: added ? '#E3F4E5' : ICE_BLUE }}>
                        {added ? <Check size={17} style={{ color: '#2F7A38' }} /> : <FileText size={16} style={{ color: BLUE }} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13.5px] font-semibold leading-tight" style={{ color: NAVY }}>{rec.label}</p>
                        <p className="flex items-center gap-1 text-[11px] font-medium mt-0.5" style={{ color: added ? '#2F7A38' : BLUE }}>
                          {added ? <><Check size={11} />{t('detect.priorTestAdded')}</> : <><Upload size={11} />{t('detect.tapToAttach') || 'Tap to attach'}</>}
                        </p>
                      </div>
                      {rec.detail && (
                        <button
                          onClick={(e) => { e.stopPropagation(); togglePrep(`rec_${i}`) }}
                          aria-label={t('detect.whyThisHelps') || 'Why this helps'}
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ border: `1px solid ${expanded ? LIGHT_BLUE : '#CBDCEB'}`, backgroundColor: expanded ? ICE_BLUE : '#fff' }}
                        >
                          <Plus size={15} style={{ color: BLUE, transform: expanded ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }} />
                        </button>
                      )}
                    </div>
                    {expanded && rec.detail && (
                      <div className="pr-3 pb-3" style={{ paddingLeft: 60 }}>
                        <p className="text-[11.5px] leading-relaxed" style={{ color: '#5A6B7B' }}>{rec.detail}</p>
                      </div>
                    )}
                    <CardFiles entries={cardFiles} busy={processingCard === cardKey} onRemove={removeFromCard} mdNameOf={mdNameOf} onBroken={refreshFileLinks} />
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* s126 A2: the suggestions past the first three, behind one quiet row. */}
        {(sugg.hidden > 0 || (showAllSuggestions && canCollapseSuggestions)) && (
          <button type="button" data-detect-more onClick={() => setShowAllSuggestions((v) => !v)}
                  className="w-full flex items-center justify-between rounded-2xl px-3.5 py-3 mb-4 text-[13px] font-semibold"
                  style={{ border: '1px solid #E8EEF4', backgroundColor: '#FAFCFE', color: NAVY }}>
            <span>{sugg.hidden > 0 ? t('detect.moreSuggestions', { defaultValue: '{{n}} more suggestions', n: sugg.hidden }) : ''}</span>
            <span style={{ color: BLUE }}>{sugg.hidden > 0 ? t('detect.show', 'Show') : t('detect.showLess', 'Show less')}</span>
          </button>
        )}

        {/* ===== YOUR UPLOADS ===== */}
        <SectionHeader icon={<Camera size={13} style={{ color: BLUE }} />} title={t('detect.yourUploads')} count={generalCount} color={BLUE} />

        {hasSubmittedDetect ? (
          /* RETURN VISIT: file list + drag-and-drop add zones */
          <div className="space-y-1.5">
            {[...general.photos.map(item => ({ item, kind: 'photos' })), ...general.videos.map(item => ({ item, kind: 'videos' }))].map(({ item, kind }) => (
              <FileRow key={item.id} item={item} mdName={mdNameOf(kind, item)} onBroken={refreshFileLinks} />
            ))}
            <div
              onClick={() => pickPhotos()}
              onDragOver={(e) => handleDragOver(e, 'photos')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'photos')}
              className={`w-full flex items-center justify-center gap-1.5 py-3.5 rounded-xl border border-dashed text-[12px] font-medium transition-all cursor-pointer ${
                dragOverType === 'photos' ? 'border-solid bg-blue-50 scale-[1.01]' : 'active:bg-blue-50'
              }`}
              style={{ borderColor: dragOverType === 'photos' ? BLUE : LIGHT_BLUE, color: BLUE }}
            >
              <Plus size={14} /><span>{dragOverType === 'photos' ? 'Drop files here' : 'Add photos or videos'}</span>
            </div>
            {general.documents.length > 0 && <div className="pt-1" />}
            {general.documents.map(item => <FileRow key={item.id} item={item} mdName={mdNameOf('documents', item)} onBroken={refreshFileLinks} />)}
            <div
              onClick={() => pickDocuments()}
              onDragOver={(e) => handleDragOver(e, 'documents')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'documents')}
              className={`w-full flex items-center justify-center gap-1.5 py-3.5 rounded-xl border border-dashed text-[12px] font-medium transition-all cursor-pointer ${
                dragOverType === 'documents' ? 'border-solid bg-blue-50 scale-[1.01]' : 'active:bg-blue-50'
              }`}
              style={{ borderColor: dragOverType === 'documents' ? BLUE : LIGHT_BLUE, color: BLUE }}
            >
              <Plus size={14} /><span>{dragOverType === 'documents' ? 'Drop files here' : 'Add documents'}</span>
            </div>
          </div>
        ) : (
          /* PRE-SHARE: upload zones + files + Nothing-to-add (non-suppressive) */
          <>
            {/* v218.0.3 Stage 2: uploaders are ALWAYS visible. "Nothing to add"
                no longer collapses them — patient can check it and still come
                back to add later. It only satisfies the hasContent gate. */}
            {/* s126 A2: the two dashed tiles became one row of two buttons (drop targets kept). */}
            <div className="grid grid-cols-2 gap-2 mb-2" data-detect-add-row>
              <button type="button" onClick={() => pickPhotos()}
                      onDragOver={(e) => handleDragOver(e, 'photos')} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'photos')}
                      className="flex items-center justify-center gap-1.5 py-3 rounded-full text-[13px] font-bold transition-all active:bg-blue-50"
                      style={{ border: `1.5px ${dragOverType === 'photos' ? 'solid' : 'dashed'} ${dragOverType === 'photos' ? BLUE : LIGHT_BLUE}`, color: NAVY, backgroundColor: '#fff' }}>
                <Camera size={15} style={{ color: BLUE }} />{t('detect.addPhotoVideoBtn', '+ Photo or video')}
              </button>
              <button type="button" onClick={() => pickDocuments()}
                      onDragOver={(e) => handleDragOver(e, 'documents')} onDragLeave={handleDragLeave} onDrop={(e) => handleDrop(e, 'documents')}
                      className="flex items-center justify-center gap-1.5 py-3 rounded-full text-[13px] font-bold transition-all active:bg-blue-50"
                      style={{ border: `1.5px ${dragOverType === 'documents' ? 'solid' : 'dashed'} ${dragOverType === 'documents' ? BLUE : LIGHT_BLUE}`, color: NAVY, backgroundColor: '#fff' }}>
                <FileText size={15} style={{ color: BLUE }} />{t('detect.addDocBtn', '+ Document')}
              </button>
            </div>

                {(general.photos.length + general.videos.length) > 0 && (
                  <div className="grid grid-cols-4 gap-2 mb-2">
                    {general.photos.map(photo => (
                      <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                        <img src={photo.data || photo.url} alt="" className="w-full h-full object-cover" onError={() => refreshFileLinks()} />
                        <button onClick={() => removeFile('photos', photo.id)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-70"><X className="text-white" size={12} /></button>
                      </div>
                    ))}
                    {general.videos.map(video => (
                      <div key={video.id} className="relative aspect-square rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center">
                        <Film className="text-gray-400" size={24} />
                        <span className="absolute bottom-0.5 left-0.5 text-[8px] text-white/80 bg-black/50 px-1 rounded">{video.name?.split('.').pop()?.toUpperCase()}</span>
                        <button onClick={() => removeFile('videos', video.id)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-70"><X className="text-white" size={12} /></button>
                      </div>
                    ))}
                    <button onClick={() => pickPhotos()} className="aspect-square border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center active:bg-gray-50"><Plus className="text-gray-400" size={20} /></button>
                  </div>
                )}

                {general.documents.length > 0 && (
                  <div className="space-y-1.5 mb-2">
                    {general.documents.map(doc => <FileRow key={doc.id} item={doc} onRemove={() => removeFile('documents', doc.id)} onBroken={refreshFileLinks} />)}
                    <button onClick={() => pickDocuments()} className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed text-[12px] font-medium active:bg-gray-50" style={{ borderColor: LIGHT_BLUE, color: BLUE }}><Plus size={14} /><span>{t('detect.addAnotherDoc')}</span></button>
                  </div>
                )}

            {/* Nothing to add — prominent, navy-when-checked, non-suppressive.
                PROD-1199: offered only while no file is added; a file added clears it. */}
            {/* s126 A2: "Nothing to add" is a quiet link that sends (Send to Doctor with no file does the same). */}
            {showNothingToAdd(uploads) && (
              <button type="button" data-detect-nothing onClick={handleSubmitDetect} disabled={isProcessing}
                      className="w-full text-center py-2.5 mt-1 text-[13px] font-semibold" style={{ color: BLUE }}>
                {t('detect.nothingToAddLink', 'I have nothing to add')}
              </button>
            )}
          </>
        )}

        {/* ===== v160: BOTTOM SECTION — Navy clinical zone ===== */}
        {/* Post-finalize: {t('detect.doctorsFindings')}.
            ★ v242.158.0 — THE PRE-FINALIZE "What to Expect at Your Visit" ZONE IS GONE
              (TestFlight feedback, 2026-09-06: a red X across the whole card). The
              ternary collapsed to a plain && with it. `detectPrep.whatToExpect` is STILL
              GENERATED AND STORED — only the surface is removed, so this is reversible and
              nothing downstream loses data. It no longer counts toward
              hasDetectPrepContent, because a case that reports "prep ready" on data that
              renders nowhere is the shape 1dk(e) is about. */}
        {(isFinalized && hasDoctorFindings) && (
          <div className="mt-5 pt-4 border-t border-gray-100">
            <SectionHeader icon={<Stethoscope size={13} className="text-white" />} title={t('detect.doctorsFindings')} count={doctorFindings.length} color={NAVY} solidIconBg />
            <div className="space-y-1.5">
              {doctorFindings.map((item, idx) => {
                const isImg = item.type?.startsWith('image/') || item.label?.includes('photo') || item.label?.includes('image')
                const isVid = item.type?.startsWith('video/')
                const displayUrl = item.url || item.preview || item.data
                return (
                  <div key={item.id || idx} onClick={() => setLightbox({ items: doctorFindings, startIndex: idx })} className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-white border border-gray-100 cursor-pointer active:bg-gray-50 transition-colors">
                    {isImg && displayUrl ? (
                      <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-200"><img src={displayUrl} alt="" className="w-full h-full object-cover" onError={() => refreshFileLinks()} /></div>
                    ) : isVid && displayUrl ? (
                      <div className="w-[72px] h-[72px] rounded-xl overflow-hidden flex-shrink-0 bg-gray-900 relative">
                        <video src={displayUrl} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                          <div className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center">
                            <div className="w-0 h-0 ml-0.5 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-l-[10px]" style={{ borderLeftColor: NAVY }} />
                          </div>
                        </div>
                      </div>
                    ) : isVid ? (
                      <div className="w-[72px] h-[72px] rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0"><Film size={24} className="text-gray-400" /></div>
                    ) : (
                      <div className="w-[72px] h-[72px] rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: NAVY }}><FileText size={24} className="text-white" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-semibold text-gray-800 truncate">{item.displayName || item.name || item.label || `Clinical file ${idx + 1}`}</p>
                      {item.label && <p className="text-[10.5px] font-medium" style={{ color: BLUE }}>{item.label}</p>}
                      {item.summary && typeof item.summary === 'string' && <p className="text-[10px] text-gray-400 truncate mt-0.5">{item.summary}</p>}
                    </div>
                    <ImageIcon size={15} className="text-gray-300 flex-shrink-0" />
                  </div>
                )
              })}
            </div>
          </div>
        )}
        </>)}
      </div>

      {/* ===== FOOTER ===== */}
      <div className="p-4 pb-7 flex-shrink-0 bg-white border-t border-gray-100" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(8px)' }}>
        {showScaleStep ? (
          <div className="flex flex-col gap-1" data-detect-scale-actions>
            <button type="button" onClick={() => saveScale(false)} disabled={!scaleDone || scaleSaving}
                    className="w-full py-3.5 rounded-full font-bold text-[15px] text-white disabled:opacity-50" style={{ backgroundColor: NAVY }}>
              {t('detect.scaleContinue', 'Continue')}
            </button>
            <button type="button" onClick={() => saveScale(true)} disabled={scaleSaving} className="w-full py-2 text-[13px] font-semibold" style={{ color: BLUE }}>
              {t('detect.scaleSkip', 'Skip — my doctor can ask at the visit')}
            </button>
          </div>
        ) : hasSubmittedDetect ? (
          <div className="flex items-center gap-2.5">
            <button onClick={() => navigate('/case/home')} className="px-5 py-3 rounded-full border-[1.5px] border-gray-200 text-[13px] font-semibold text-gray-400 flex-shrink-0 active:bg-gray-50">{t('detect.back')}</button>
            {hasNewFiles ? (
              <button onClick={handleSaveNewFiles} disabled={isProcessing} className="flex-1 py-3 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 active:opacity-90" style={{ backgroundColor: BLUE }}>
                <Check size={16} strokeWidth={2.5} />{t('detect.saveNewFiles')}
              </button>
            ) : (
              <button
                onClick={() => navigate('/case/home')}
                className="flex-1 py-3 rounded-full text-white font-bold text-[14px] flex items-center justify-center gap-2 active:opacity-90 breathe-blue"
                style={{ backgroundColor: BLUE }}
              >
                <ChevronRight size={16} strokeWidth={2.5} />
                {t('detect.continueNoNewFiles') || 'Nothing to add — continue'}
              </button>
            )}
          </div>
        ) : (
          <button data-detect-send onClick={handleSubmitDetect} disabled={isProcessing} className={`w-full py-3.5 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 transition-all text-white active:opacity-90 breathe-blue`} style={{ backgroundColor: BLUE }}>
            <Check size={18} strokeWidth={2.5} />{t('detect.sendToDoctor') || 'Send to Doctor'}
          </button>
        )}
      </div>

      {/* Submit Overlay — shared component with FAB suppression */}
      <SubmitOverlay
        phase={submitPhase}
        processingTitle={hasSubmittedDetect ? 'Saving your files...' : 'Submitting your evidence...'}
        processingSubtitle={hasSubmittedDetect ? 'Your doctor will see them right away' : 'Moving to Diagnose...'}
        successTitle={hasSubmittedDetect ? 'Files Saved!' : 'Detect Complete!'}
        successSubtitle={hasSubmittedDetect ? 'Heading back to your case...' : 'Preparing your case...'}
      />

      <DeleteCaseModal canDelete={!isHeldByDoctor(currentCase)} isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onDelete={handleDeleteCase} onSaveAsDraft={handleSaveAsDraft} caseName={currentCase?.focusAreaName || currentCase?.focusArea?.name || ''} />
      {lightbox && <MediaLightbox items={lightbox.items} startIndex={lightbox.startIndex} onClose={() => setLightbox(null)} />}
    </div>
  )
}
